import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, MessageCircle, Mail, Calendar, FileText, Edit3, Tag } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PriorityBadge, SourceBadge, StageBadge } from '@/components/pipeline/Badges';
import ContactSlideOver from '@/components/pipeline/ContactSlideOver';
import LinkedContracts from '@/components/contacts/LinkedContracts';
import MatchedProjects from '@/components/contacts/MatchedProjects';
import { formatAED, formatDate, formatDateTime, initials, relativeTime } from '@/lib/format';
import { STAGES } from '@/lib/constants';

export default function ContactDetailPage() {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const { profile } = useAuth();
    const [contact, setContact] = useState(null);
    const [activities, setActivities] = useState([]);
    const [agents, setAgents] = useState([]);
    const [edit, setEdit] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [noteType, setNoteType] = useState('note');

    const load = useCallback(async () => {
        const [{ data: c }, { data: acts }, { data: ag }] = await Promise.all([
            supabase.from('pipeline_contacts').select('*').eq('id', contactId).maybeSingle(),
            supabase.from('pipeline_activities').select('*').eq('contact_id', contactId).order('created_at', { ascending: false }),
            supabase.from('profiles').select('id, full_name, email, role, is_active'),
        ]);
        setContact(c || null);
        setActivities(acts || []);
        setAgents(ag || []);
    }, [contactId]);

    useEffect(() => { load(); }, [load]);

    const updateStage = async (stage) => {
        if (!contact || stage === contact.pipeline_stage) return;
        const { error } = await supabase.from('pipeline_contacts').update({ pipeline_stage: stage }).eq('id', contact.id);
        if (error) { toast.error(error.message); return; }
        await supabase.from('pipeline_activities').insert({
            contact_id: contact.id, organization_id: contact.organization_id, agent_id: profile.id,
            activity_type: 'status_change', description: `Stage changed: ${contact.pipeline_stage} → ${stage}`,
            old_stage: contact.pipeline_stage, new_stage: stage,
        });
        toast.success(`Moved to ${stage}`);
        load();
    };

    const addActivity = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        await supabase.from('pipeline_activities').insert({
            contact_id: contact.id, organization_id: contact.organization_id, agent_id: profile.id,
            activity_type: noteType, description: newNote.trim(),
        });
        if (noteType === 'call' || noteType === 'whatsapp' || noteType === 'email') {
            await supabase.from('pipeline_contacts').update({ last_contacted_at: new Date().toISOString() }).eq('id', contact.id);
        }
        setNewNote('');
        toast.success('Activity logged');
        load();
    };

    const onCall = () => { if (contact?.phone) window.open(`tel:${contact.phone}`); addActivity({ preventDefault: () => {} }); };
    const onWhatsApp = () => {
        const phone = (contact?.whatsapp || contact?.phone || '').replace(/[^0-9]/g, '');
        if (phone) Object.assign(document.createElement('a'), { href: `https://wa.me/${phone}`, target: '_blank', rel: 'noopener noreferrer' }).click();
    };
    const onEmail = () => { if (contact?.email) window.open(`mailto:${contact.email}`); };

    const uploadDoc = async (e, field) => {
        const file = e.target.files?.[0];
        if (!file || !contact) return;
        const ext = file.name.split('.').pop();
        const path = `${contact.organization_id}/${contact.id}/${field}-${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('kyc-docs').upload(path, file, { upsert: true });
        if (error) {
            if (error.message?.toLowerCase().includes('bucket') || error.statusCode === '404') {
                toast.error('Storage bucket "kyc-docs" not found. Create it in Supabase dashboard → Storage.');
            } else toast.error(error.message);
            return;
        }
        const { data: pub } = supabase.storage.from('kyc-docs').getPublicUrl(path);
        await supabase.from('pipeline_contacts').update({ [field]: pub.publicUrl }).eq('id', contact.id);
        await supabase.from('pipeline_activities').insert({
            contact_id: contact.id, organization_id: contact.organization_id, agent_id: profile.id,
            activity_type: 'document_uploaded', description: `Uploaded ${field === 'emirates_id_url' ? 'Emirates ID' : 'Passport'}`,
        });
        toast.success('Uploaded');
        load();
    };

    const deleteDoc = async (field, url) => {
        const path = url?.split('/object/public/kyc-docs/')[1];
        if (path) await supabase.storage.from('kyc-docs').remove([path]);
        await supabase.from('pipeline_contacts').update({ [field]: null }).eq('id', contact.id);
        toast.success('Document deleted');
        load();
    };

    const assignAgent = async (agentId) => {
        const { error } = await supabase.from('pipeline_contacts').update({ assigned_to: agentId || null }).eq('id', contact.id);
        if (error) { toast.error(error.message); return; }
        toast.success('Agent updated');
        load();
    };

    if (!contact) return <div className="p-6 text-gray-500">Loading…</div>;
    const agent = contact.assigned_profile || agents.find((a) => a.id === contact.assigned_to);
    const activeAgents = agents.filter((a) => a.is_active !== false);
    const isClient = ['Closed Won', 'Active Client', 'Renewal Due', 'Repeat Investor'].includes(contact.pipeline_stage);

    return (
        <div className="max-w-[1400px] mx-auto p-4 lg:p-6" data-testid="contact-detail-page">
            <Link to="/pipeline" className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-aks-navy mb-4">
                <ArrowLeft className="h-4 w-4" /> Back to pipeline
            </Link>

            <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="h-14 w-14 rounded-full bg-aks-navy text-white flex items-center justify-center text-lg font-bold">
                        {initials(contact.full_name)}
                    </div>
                    <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-2xl font-bold tracking-tight text-aks-text">{contact.full_name}</h1>
                            <PriorityBadge value={contact.priority} />
                            <SourceBadge value={contact.source} detail={contact.source_detail} />
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-[13px] text-gray-500 mt-1">
                            {contact.phone && <span>{contact.phone}</span>}
                            {contact.email && <span>· {contact.email}</span>}
                        </div>
                        <div className="text-[13px] text-gray-500 mt-0.5">
                            Assigned Agent: <span className="font-medium text-aks-text">{agent ? (agent.full_name || agent.email) : 'Unassigned'}</span>
                        </div>
                        <div className="mt-2 inline-block">
                            <select value={contact.pipeline_stage} onChange={(e) => updateStage(e.target.value)} className="h-8 px-2 rounded-md border border-gray-300 text-[12px] font-semibold text-aks-text bg-white" data-testid="detail-stage-select">
                                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <ActionBtn onClick={onCall} icon={<Phone className="h-4 w-4" />}>Call</ActionBtn>
                        <ActionBtn onClick={onWhatsApp} icon={<MessageCircle className="h-4 w-4" />} color="#25D366">WhatsApp</ActionBtn>
                        <ActionBtn onClick={onEmail} icon={<Mail className="h-4 w-4" />}>Email</ActionBtn>
                        <button onClick={() => setEdit(true)} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-aks-navy text-white text-[13px] font-semibold" data-testid="detail-edit-btn">
                            <Edit3 className="h-4 w-4" /> Edit
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <Section title="Property interest">
                        <Grid>
                            <Info label="Budget" value={formatAED(contact.budget_aed)} />
                            <Info label="Type" value={contact.property_type ? <span className="capitalize">{contact.property_type}</span> : '—'} />
                            <Info label="Bedrooms" value={contact.bedrooms_preference || '—'} />
                            <Info label="Emirate" value={contact.emirate_preference || '—'} />
                            <Info label="Nationality" value={contact.nationality || '—'} />
                            <Info label="WhatsApp" value={contact.whatsapp || '—'} />
                        </Grid>
                    </Section>

                    {isClient && (
                        <Section title="Client details">
                            <Grid>
                                <Info label="Client type" value={contact.client_type ? <span className="capitalize">{contact.client_type}</span> : '—'} />
                                <Info label="Total transactions" value={formatAED(contact.total_transaction_value_aed)} />
                                <Info label="Golden visa eligible" value={contact.golden_visa_eligible ? 'Yes' : 'No'} />
                                {contact.client_type === 'tenant' && (<>
                                    <Info label="Lease start" value={formatDate(contact.lease_start_date)} />
                                    <Info label="Lease end" value={formatDate(contact.lease_end_date)} />
                                    <Info label="Monthly rent" value={formatAED(contact.monthly_rent_aed)} />
                                </>)}
                            </Grid>
                        </Section>
                    )}

                    <Section title="KYC documents">
                        <Grid>
                            <DocUpload label="Emirates ID" url={contact.emirates_id_url} onUpload={(e) => uploadDoc(e, 'emirates_id_url')} onDelete={() => deleteDoc('emirates_id_url', contact.emirates_id_url)} />
                            <DocUpload label="Passport" url={contact.passport_url} onUpload={(e) => uploadDoc(e, 'passport_url')} onDelete={() => deleteDoc('passport_url', contact.passport_url)} />
                        </Grid>
                    </Section>

                    <Section title="Notes">
                        <p className="text-[13px] text-gray-700 whitespace-pre-wrap">{contact.notes || <span className="text-gray-400">No notes yet</span>}</p>
                    </Section>

                    <MatchedProjects contact={contact} />

                    <LinkedContracts contact={contact} />
                </div>

                <div className="space-y-4">
                    <Section title="Follow-up">
                        <Info label="Next follow-up" value={formatDateTime(contact.next_followup_at) || '—'} />
                        <Info label="Last contacted" value={contact.last_contacted_at ? relativeTime(contact.last_contacted_at) : '—'} />
                        {contact.followup_notes && <p className="text-[12px] text-gray-600 mt-2">{contact.followup_notes}</p>}
                    </Section>

                    <Section title="Activity timeline" testId="activity-timeline">
                        <form onSubmit={addActivity} className="mb-3">
                            <div className="flex flex-wrap gap-1 mb-2">
                                {['note', 'call', 'whatsapp', 'email', 'viewing'].map((t) => (
                                    <button type="button" key={t} onClick={() => setNoteType(t)} className={`h-7 px-2 rounded-full text-[11px] font-semibold capitalize border ${noteType === t ? 'bg-aks-navy text-white border-aks-navy' : 'border-gray-300 text-gray-600 hover:border-aks-gold/60'}`} data-testid={`note-type-${t}`}>{t}</button>
                                ))}
                            </div>
                            <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} rows={2} placeholder="Log a note, call summary, viewing…" className="w-full p-2 border border-gray-300 rounded-md text-[13px] outline-none focus:border-aks-gold aks-focus-ring" data-testid="new-activity-input" />
                            <button type="submit" disabled={!newNote.trim()} className="mt-2 w-full h-9 rounded-md bg-aks-gold text-aks-navy font-semibold text-[13px] disabled:opacity-50" data-testid="log-activity-btn">Log activity</button>
                        </form>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                            {activities.length === 0 ? <div className="py-6 text-center text-[12px] text-gray-400">No activity yet</div> : activities.map((a) => (
                                <div key={a.id} className="flex gap-2 py-2 border-b border-gray-50 last:border-b-0" data-testid={`activity-${a.id}`}>
                                    <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-700 flex-shrink-0 uppercase">{a.activity_type.slice(0, 2)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[12px] text-aks-text">{a.description}</div>
                                        <div className="text-[10px] text-gray-400 mt-0.5">{a.activity_type} · {relativeTime(a.created_at)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                </div>
            </div>

            <ContactSlideOver open={edit} onClose={() => setEdit(false)} contact={contact} agents={agents} onSaved={async () => {
                    const { data: fresh } = await supabase.from('pipeline_contacts').select('*, assigned_to:profiles(id, full_name)').eq('id', contactId).maybeSingle();
                    if (fresh) {
                        const assignedProfile = typeof fresh.assigned_to === 'object' ? fresh.assigned_to : null;
                        setContact({ ...fresh, assigned_to: assignedProfile?.id ?? fresh.assigned_to, assigned_profile: assignedProfile });
                    }
                }} onDeleted={() => navigate('/pipeline')} />
        </div>
    );
}

function ActionBtn({ icon, children, onClick, color }) {
    return (
        <button onClick={onClick} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-gray-300 hover:border-aks-gold text-[13px] font-semibold text-aks-text" style={color ? { color } : {}}>
            {icon} {children}
        </button>
    );
}
function Section({ title, children, testId }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-3">{title}</div>
            {children}
        </div>
    );
}
function Grid({ children }) { return <div className="grid grid-cols-2 gap-y-3 gap-x-4">{children}</div>; }
function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
            <div className="text-[14px] text-aks-text mt-0.5">{value}</div>
        </div>
    );
}
function DocUpload({ label, url, onUpload, onDelete }) {
    const [confirming, setConfirming] = React.useState(false);
    const handleView = async () => {
        const path = url?.split('/object/public/kyc-docs/')[1];
        if (!path) return;
        const { data, error } = await supabase.storage.from('kyc-docs').createSignedUrl(path, 60);
        if (!error && data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    };
    return (
        <div className="col-span-2 flex items-center justify-between p-3 border border-gray-200 rounded-md">
            <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-[13px] font-medium">{label}</span>
                {url && <button type="button" onClick={handleView} className="text-[11px] text-aks-navy hover:text-aks-gold underline">View</button>}
                {url && !confirming && (
                    <button type="button" onClick={() => setConfirming(true)} className="text-[11px] text-red-500 hover:text-red-700 underline">Delete</button>
                )}
                {url && confirming && (
                    <span className="flex items-center gap-1.5">
                        <span className="text-[11px] text-red-600 font-medium">Delete?</span>
                        <button type="button" onClick={() => { setConfirming(false); onDelete(); }} className="text-[11px] font-semibold text-red-600 hover:text-red-800 underline">Yes</button>
                        <button type="button" onClick={() => setConfirming(false)} className="text-[11px] text-gray-500 hover:text-gray-700 underline">No</button>
                    </span>
                )}
            </div>
            <label className="h-8 px-3 rounded-md border border-gray-300 text-[12px] font-semibold cursor-pointer hover:border-aks-gold inline-flex items-center">
                {url ? 'Replace' : 'Upload'}
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onUpload} />
            </label>
        </div>
    );
}
